import * as B from '@blueprintjs/core';
import * as S from '@blueprintjs/select';
import * as I from 'immutable';
import React from 'react';

import {
  LibraryDataset,
  LibraryDatasetType,
  OverlayDataset,
} from 'app/components/Library/LibraryTypes';
import {LibraryItemDetailsModal} from 'app/components/Library/modal';
import {ApiOrganization, ApiOverlay} from 'app/modules/Remote/Organization';
import {getOrgPrefix} from 'app/utils/organizationUtils';

import {DrawPropertiesState} from './ManagePropertiesDraw';
import cs from './styles.module.scss';

interface Props {
  drawPropertiesState: DrawPropertiesState;
  organization: I.ImmutableOf<ApiOrganization>;
  loadingOverlays: boolean;
}

type SelectItem =
  | {kind: 'upstream'; data: LibraryDataset<OverlayDataset>}
  | {kind: 'org'; data: ApiOverlay};

const ManagePropertiesParcelOverlaySelector: React.FC<Props> = ({
  drawPropertiesState,
  organization,
  loadingOverlays,
}) => {
  const [openModalOverlayId, setOpenModalOverlayId] = React.useState<number | null>(null);

  const {
    upstreamParcelOverlays: curatedUpstreamOverlays,
    orgOverlays,
    selectedOverlayId,
    setSelectedOverlayId,
  } = drawPropertiesState;

  const allItems: SelectItem[] = React.useMemo(
    () => [
      ...(curatedUpstreamOverlays ?? []).map((o) => ({kind: 'upstream' as const, data: o})),
      ...(orgOverlays ?? [])
        .slice()
        .sort((a, b) => a.name.localeCompare(b.name))
        .map((o) => ({kind: 'org' as const, data: o})),
    ],
    [curatedUpstreamOverlays, orgOverlays]
  );

  if (loadingOverlays) {
    return <B.Spinner />;
  }

  const selectedItem = allItems.find((i) => i.data.featureCollectionId === selectedOverlayId);

  return (
    <div className={cs.overlaySelector}>
      <B.Callout intent="primary" icon={false}>
        {!selectedOverlayId ? (
          <div>
            Choose an overlay from the dropdown. Then click polygons on the map to add properties.
          </div>
        ) : (
          <>
            <div>
              Click on features in the overlay on the map to add properties. Shift-click to add
              another feature to the same property.
            </div>
            {selectedItem?.kind === 'upstream' && selectedItem.data.mapHelperText && (
              <div>{selectedItem.data.mapHelperText}</div>
            )}
          </>
        )}
      </B.Callout>

      <S.Select<SelectItem>
        items={allItems}
        itemRenderer={(item, {handleClick, modifiers}) => (
          <B.MenuItem
            key={item.data.featureCollectionId}
            text={item.data.name}
            onClick={handleClick}
            active={item.data.featureCollectionId === selectedOverlayId}
            disabled={modifiers.disabled}
            className={item.kind === 'upstream' ? cs.upstreamMenuItem : undefined}
            labelElement={
              item.kind === 'upstream' ? (
                <B.Button
                  type="button"
                  className={cs.menuItemInfoButton}
                  onClick={(e) => {
                    e.stopPropagation();
                    e.preventDefault();
                    setOpenModalOverlayId(item.data.featureCollectionId);
                  }}
                  minimal
                  icon={<B.Icon icon="info-sign" />}
                  small
                />
              ) : undefined
            }
          />
        )}
        itemPredicate={(query, item) => item.data.name.toLowerCase().includes(query.toLowerCase())}
        itemListRenderer={({filteredItems, itemsParentRef, renderItem}) => {
          const upstreamItems = filteredItems.filter((i) => i.kind === 'upstream');
          const orgItems = filteredItems.filter((i) => i.kind === 'org');

          return (
            <B.Menu ulRef={itemsParentRef}>
              {filteredItems.length === 0 && <B.MenuItem disabled text="No results." />}
              {upstreamItems.length > 0 && (
                <>
                  <B.MenuDivider title="Upstream Overlays" />
                  {upstreamItems.map((item) => renderItem(item, filteredItems.indexOf(item)))}
                </>
              )}
              {orgItems.length > 0 && (
                <>
                  <B.MenuDivider title="Your Overlays" />
                  {orgItems.map((item) => renderItem(item, filteredItems.indexOf(item)))}
                </>
              )}
              <B.MenuDivider />
              <B.MenuItem
                icon="upload"
                text="Upload a custom overlay…"
                href={`/${getOrgPrefix(organization)}/admin/manageProperties/overlay/`}
                target="_blank"
                rel="noreferrer"
              />
            </B.Menu>
          );
        }}
        onItemSelect={(item) => setSelectedOverlayId?.(item.data.featureCollectionId)}
        popoverProps={{minimal: true}}
      >
        <B.Button
          text={selectedItem?.data.name ?? 'Select an overlay'}
          rightIcon="caret-down"
          fill
        />
      </S.Select>

      {(curatedUpstreamOverlays ?? []).map((overlay) => (
        <LibraryItemDetailsModal
          key={overlay.featureCollectionId}
          isOpen={openModalOverlayId === overlay.featureCollectionId}
          onClose={() => setOpenModalOverlayId(null)}
          actionButton={<></>}
          name={overlay.name}
          source={overlay.source}
          description={overlay.description}
          license={overlay.license}
          tags={overlay.tags}
          geographicExtent={overlay.geographicExtent}
          thumbnailUrl={overlay.thumbnailUrl}
          type={LibraryDatasetType.OVERLAY}
        />
      ))}
    </div>
  );
};

export default ManagePropertiesParcelOverlaySelector;
